import React, { useEffect, useState } from 'react';
import { addClaimNote, editClaimNote, updateClaimNotes, ClaimNoteEntry } from '../api/claims';

interface ClaimNotesPanelProps {
  claimId: string;
  noteEntries?: ClaimNoteEntry[];
  /** Older free-text notes, shown as the "Earlier notes" row. */
  legacyNotes?: string | null;
  onNoteEntriesChanged: (noteEntries: ClaimNoteEntry[]) => void;
  onLegacyNotesChanged: (notes: string) => void;
}

const LEGACY_ID = '__earlier__';

export const formatNoteTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }).replace(/\bam\b/, 'AM').replace(/\bpm\b/, 'PM');
};

const editedTooltip = (note: ClaimNoteEntry): string | undefined => {
  if (!note.editedAt) return undefined;
  const times = (note.edits || []).length;
  const extra = times > 1 ? ` (edited ${times} times)` : '';
  return `Edited ${formatNoteTime(note.editedAt)} by ${note.editedBy}${extra}`;
};

const boxStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e0e0e0', fontSize: '14px',
  lineHeight: '1.5', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
};

const buttonStyle = (enabled: boolean, colour: string): React.CSSProperties => ({
  padding: '6px 14px', borderRadius: '6px', border: 'none', color: 'white', fontSize: '13px', fontWeight: 500,
  backgroundColor: enabled ? colour : '#ccc', cursor: enabled ? 'pointer' : 'not-allowed',
});

const linkButton: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0, color: '#2563eb', fontSize: '12px', cursor: 'pointer',
};

/**
 * Claim notes (Freshdesk #570). One box: each note is a row with when it was
 * written and by whom on the left, the note on the right. Notes can be
 * edited one at a time; an edited note shows "edited", and hovering over it
 * shows when and by whom. Every add and edit is also in the audit log.
 */
const ClaimNotesPanel: React.FC<ClaimNotesPanelProps> = ({
  claimId, noteEntries, legacyNotes, onNoteEntriesChanged, onLegacyNotesChanged,
}) => {
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft('');
    setEditingId(null);
    setError(null);
  }, [claimId]);

  const rows = [...(noteEntries || [])].reverse();
  const legacy = (legacyNotes || '').trim();

  const run = async (action: () => Promise<void>, failure: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err: any) {
      setError(err?.response?.data?.error || failure);
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = () => {
    const text = draft.trim();
    if (!text || busy) return;
    run(async () => {
      const result = await addClaimNote(claimId, text);
      onNoteEntriesChanged(result.noteEntries);
      setDraft('');
    }, 'Failed to add note');
  };

  const startEdit = (id: string, text: string) => {
    setEditingId(id);
    setEditText(text);
    setError(null);
  };

  const saveEdit = () => {
    if (!editingId || busy) return;
    const id = editingId;
    if (id !== LEGACY_ID && !editText.trim()) {
      setError('A note can’t be empty');
      return;
    }
    run(async () => {
      if (id === LEGACY_ID) {
        await updateClaimNotes(claimId, editText);
        onLegacyNotesChanged(editText);
      } else {
        onNoteEntriesChanged(await editClaimNote(claimId, id, editText.trim()));
      }
      setEditingId(null);
    }, 'Failed to save the note');
  };

  const renderText = (id: string, text: string) => (editingId === id ? (
    <div>
      <textarea
        aria-label="Edit note"
        value={editText}
        onChange={(e) => setEditText(e.target.value)}
        style={{ ...boxStyle, minHeight: '70px', backgroundColor: '#fff' }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '6px' }}>
        <button type="button" onClick={() => setEditingId(null)} disabled={busy} style={buttonStyle(!busy, '#6b7280')}>Cancel</button>
        <button type="button" onClick={saveEdit} disabled={busy} style={buttonStyle(!busy, '#2563eb')}>
          {busy ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  ) : (
    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#111827' }}>{text}</div>
  ));

  const rowStyle = (last: boolean): React.CSSProperties => ({
    display: 'grid', gridTemplateColumns: '190px 1fr auto', gap: '12px', alignItems: 'start',
    padding: '10px 12px', borderBottom: last ? 'none' : '1px solid #eef0f3', fontSize: '14px',
  });

  const total = rows.length + (legacy ? 1 : 0);

  return (
    <div className="info-section" style={{ marginTop: '20px' }}>
      <h4>Notes</h4>

      <div style={{ marginTop: '10px' }}>
        <label htmlFor="claim-new-note" style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>
          Add a note
        </label>
        <textarea
          id="claim-new-note"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type a note about this claim..."
          maxLength={5000}
          style={{ ...boxStyle, minHeight: '70px' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', gap: '10px' }}>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>Saved with your email and the date and time.</span>
          <button type="button" onClick={handleAdd} disabled={!draft.trim() || busy} style={buttonStyle(!!draft.trim() && !busy, '#4CAF50')}>
            {busy && !editingId ? 'Saving...' : 'Add note'}
          </button>
        </div>
      </div>

      {/* Scrolls once there are more than a handful of notes, so it doesn't take over the page. */}
      <div role="list" aria-label="Saved notes" style={{
        marginTop: '16px', border: '1px solid #e5e7eb', borderRadius: '6px', backgroundColor: '#fff',
        maxHeight: '360px', overflowY: 'auto',
      }}>
        {rows.map((note, i) => (
          <div role="listitem" key={note.id} title={editedTooltip(note)} style={rowStyle(i === total - 1)}>
            <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.4 }}>
              <div style={{ color: '#374151', fontWeight: 500 }}>{formatNoteTime(note.createdAt)}</div>
              <div style={{ wordBreak: 'break-all' }}>{note.createdBy}</div>
              {note.editedAt && <div style={{ fontStyle: 'italic', marginTop: '2px' }}>edited</div>}
            </div>
            {renderText(note.id, note.text)}
            {editingId !== note.id && (
              <button type="button" aria-label="Edit this note" onClick={() => startEdit(note.id, note.text)} disabled={busy} style={linkButton}>Edit</button>
            )}
          </div>
        ))}
        {legacy && (
          <div role="listitem" style={{ ...rowStyle(true), backgroundColor: '#fafafa' }}>
            <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.4 }}>
              <div style={{ color: '#374151', fontWeight: 500 }}>Earlier notes</div>
              <div>No date recorded</div>
            </div>
            {renderText(LEGACY_ID, legacy)}
            {editingId !== LEGACY_ID && (
              <button type="button" aria-label="Edit earlier notes" onClick={() => startEdit(LEGACY_ID, legacy)} disabled={busy} style={linkButton}>Edit</button>
            )}
          </div>
        )}
        {!total && <div style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280' }}>No notes yet.</div>}
      </div>

      {error && <div role="alert" style={{ color: '#d32f2f', fontSize: '12px', marginTop: '6px' }}>{error}</div>}
    </div>
  );
};

export default ClaimNotesPanel;
